import * as Sentry from '@sentry/browser'
import api from '@/services/api'
import cookie from '@/services/utilities/cookie'
import account from '@/services/api/account.js'
import { getDeepProperty } from '@/helpers/deepObject.js'
import * as types from './mutation-types.js'

const getAccountsCollection = (path) => {
  return api.get(path).then(({data}) => data.collection)
}

const actions = {
  acceptUserInvite ({commit}, path) {
    return api.post(path).then(({data}) => data)
  },
  clearOrgPodcasts ({state, commit}) {
    commit(types.CLEAR_ORG_PODCASTS)
  },
  clearUserInvites ({commit}) {
    commit(types.CLEAR_USER_INVITES)
  },
  async createPrivateApp ({state, commit}, {account, payload}) {
    payload.account = account.id

    return api.post.data(account.private_apps, payload)
      .then((data) => {
        this.dispatch('fetchPrivateApps', account)

        return data
      })
  },
  async deleteAuthorizedApp ({state, commit}, app) {
    return api.delete.data(app)
      .then(() => {
        this.dispatch('fetchAuthorizedApps', this.getters.user)
      })
  },
  async deletePrivateApp ({state, commit}, app) {
    return api.delete.data(app)
      .then(() => {
        this.dispatch('fetchAuthorizedApps', this.getters.user)
        this.dispatch('fetchPrivateApps', this.getters.defaultAccount)
      })
  },
  deleteUser ({commit, state}, user) {
    return api.delete.data(user)
      .then(() => {
        cookie.eraseCookies()
      })
  },
  async fetchAccountApiKey ({commit, state}, account) {
    let apiKey = getDeepProperty(account, 'api_key')
    let accountApiKey = await api.get.data(apiKey)

    commit(types.SET_ACCOUNT_API_KEY, accountApiKey)
  },
  async fetchAuthorizedApps ({commit}, user) {
    let authorizations = getDeepProperty(user, 'oauth_authorizations')
    let apps = await api.get.data(authorizations)

    commit(types.SET_AUTHORIZED_APPS, {apps})

    return apps
  },
  async fetchNavigation  ({commit, state}) {
    const nav = getDeepProperty(state.currentAccount, 'nav')
    const navigation = await api.get.data(nav)
    commit(types.SET_ACCOUNT_NAV, navigation)
    return navigation
  },
  // THIS IS MEANT TO GET THE PRIVATE APPS ON THE ACCOUNT
  async fetchPrivateApps ({commit}, account) {
    // Loads all the created Private Apps
    let clients = getDeepProperty(account, 'oauth_clients')

    let apps = await api.get.data(clients).then(async (data) => {
      let appPromises = data.collection.map(app => api.get.data(app))
      let apps = await Promise.all(appPromises)

      data.collection = apps

      return data
    })

    commit(types.SET_PRIVATE_APPS, {account, apps})
  },
  async fetchPrivateAppSecret ({commit}, app) {
    // Not storing this, just going to return the result for security!
    return api.get(app.client_secret.href).then(async ({ data }) => data.client_secret)
  },
  async fetchUser ({commit, state}) {
    let user = await api.get('/current_user?default_podcast=true').then(async ({ data }) => {
      if (data.accounts.href) {
        // Get all accounts on this user
        let accountsCollection = await getAccountsCollection('/accounts')

        // Get all accounts details for each account on this user
        let accounts = accountsCollection.map(async acct => {
          let accountDetails = await api.get.data(acct)

          // Add permission list only returned from /accounts
          accountDetails.permission = acct.permission

          // Load in the feature sets
          if (accountDetails && accountDetails.features && accountDetails.features.href) {
            accountDetails.features = await api.get(accountDetails.features.href + '?level=account').then(({data}) => data)
          }

          return accountDetails
        })

        const collection = await Promise.all(accounts)
        data.accounts = {...data.accounts, collection}
      } else {
        data.accounts.collection = []
      }

      return data
    }).catch(e => null)

    commit(types.SET_USER, user)

    // Load the user into error logging
    if (user && user.email) {
      Sentry.configureScope((scope) => {
        scope.setUser({
          email: user.email,
          id: user.id
        })
      })
    }

    return user
  },
  async getFeatureFlags ({ commit }) {
    const req = await api.get('/feature_flags')
      .then(async ({ data }) => {
        return data
      })
    // TODO: add ability to set feature flags in local storage?
    commit(types.SET_FEATURE_FLAGS, req)
  },
  async getNetworkSummary ({ state, commit }, options) {
    return await account.getNetworkSummary(options)
  },
  async getOrgPodcasts ({state, commit}, {options, accountId}) {
    const limit = options.limit || 10
    options = { ...options, limit: limit, account: accountId }

    let result = await account.getOrgPodcasts(options)
    let podcastList = result.collection
    let podcastCount = result.count

    commit(types.SET_ORG_PODCASTS_COUNT, podcastCount)
    commit(types.SET_ORG_PODCASTS, podcastList)
    if (!options.search) {
      commit(types.SET_ORG_PODCASTS_TOTAL, podcastCount)
    }

    return result
  },
  async getPodcastDetails ({commit, state}, podcast) {
    return api.get.data(podcast).then(async (data) => {
      let subscription = await api.get.data(data.subscription_item)
      data.subscription_item = {
        ...data.subscription_item,
        ...subscription
      }

      return data
    })
  },
  async getTwoFactor ({commit}, path) {
    const results = await api.get.data(path)

    commit(types.SET_TWO_FACTOR, results)
    return results
  },
  async getUserInvites ({commit}, path) {
    const results = await api.get.data(path)

    commit(types.SET_USER_INVITES, results)
    return results
  },
  async refreshAccount ({commit, state}, account) {
    let refreshedAccount = await api.get.data(account)
    refreshedAccount = {
      ...refreshedAccount,
      permission: account.permission,
      features: account.features
    }
    commit(types.SET_ACCOUNT, refreshedAccount)
  },
  async requestTwoFactor ({state}, {path, payload}) {
    return api.post(path, payload).then(({data}) => data)
  },
  setNotification ({commit}, notification) {
    commit(types.SET_NOTIFICATION, notification)
  },
  async updateCardDetails ({commit, state}, {card, cardToken, couponCode}) {
    let action = card.update ? 'put' : 'post'

    await api[action].data(card, {card_token: cardToken, coupon_code: couponCode})
  },
  updateInvoiceDetails ({commit}, {account, payload}) {
    return api.put.data(account, payload).then((data) => data)
  },
  async updatePrivateApp ({ state, commit }, {app, payload}) {
    return api.put.data(app, payload)
      .then((data) => {
        this.dispatch('fetchPrivateApps', this.getters.defaultAccount)
      })
  },
  updateUserEmail ({commit, state}, {user, payload}) {
    return api.post(user.edit.action + '/email', payload)
      .then((data) => {
        return data
      })
      .catch((err) => {
        throw err
      })
  },
  async updateUserDetails ({commit, state}, {user, payload}) {
    let data = await api.put.data(user, payload).then((data) => data)

    this.dispatch('fetchUser')

    return data
  },
  async updateUserPreferences ({state, commit}, {preferences, payload}) {
    let data = await api.put.data(preferences, payload).then((data) => data)

    commit(types.SET_USER_PREFERENCES, data)

    return data
  }
}

export default actions
